import React from "react";
import styled, { keyframes, css } from "styled-components";
import Modal from "@material-ui/core/Modal";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { T, FONTS, FS, BP } from "../tokens";
import { IcoLayers } from "./Icons";

export const Icons = {
  Layers: ({ size = 20, color = "currentColor" }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  ),
  X: ({ size = 16, color = "currentColor" }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Check: ({ size = 12, color = "currentColor" }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  ChevronLeft: ({ size = 14, color = "currentColor" }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  ChevronRight: ({ size = 14, color = "currentColor" }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
};

const slideUp = keyframes`
  from { transform: translateY(40px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

export const ModalShell = ({ open, onClose, children }) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 300,
        style: {
          backgroundColor: "rgba(15,23,42,0.6)",
          boxShadow: "0 32px 80px rgba(41, 52, 97, 0.22)",
          // backdropFilter: "blur(4px)",
        },
      }}
    >
      <Fade in={open} timeout={300}>
        <ModalPaper $fullScreen={isMobile}>{children}</ModalPaper>
      </Fade>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;

  @media (max-width: ${BP.mobile}) {
    padding: 0;
    align-items: flex-end;
  }
`;

const ModalPaper = styled.div`
  width: 100%;
  max-width: 780px;
  max-height: calc(100vh - 32px);
  background: ${T.white};
  border-radius: 20px;
  box-shadow:
    0 32px 80px rgba(15, 23, 42, 0.24),
    0 8px 24px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideUp} 0.32s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  outline: none;

  ${(p) =>
    p.$fullScreen &&
    css`
      max-width: 100%;
      max-height: 100vh;
      border-radius: 20px 20px 0 0;
    `}
`;

export const ModalHeader = ({ step, stepLabels, onClose }) => (
  <HeaderWrap>
    <HeaderIconBox>
      <IcoLayers size={18} color="#fff" />
    </HeaderIconBox>
    <HeaderText>
      <ModalTitle>Create Trial Bundle</ModalTitle>
      <ModalSubtitle>
        Step {step} of {stepLabels.length} — {stepLabels[step - 1]}
      </ModalSubtitle>
    </HeaderText>
    <CloseBtn onClick={onClose} title="Close" aria-label="Close dialog">
      <Icons.X size={14} color={T.mute} />
    </CloseBtn>
  </HeaderWrap>
);

const HeaderWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid ${T.line};
  background: ${T.white};
  flex-shrink: 0;

  @media (max-width: ${BP.tablet}) {
    padding: 14px 16px;
  }
`;

const HeaderIconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${T.teal} 0%, ${T.tealDark} 100%);
  display: grid;
  place-items: center;
  flex-shrink: 0;
  box-shadow: 0 4px 14px rgba(0, 131, 140, 0.35);
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ModalTitle = styled.h2`
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.lg};
  font-weight: 700;
  color: ${T.navy};
  margin: 0 0 2px;
  line-height: 1.3;

  @media (max-width: ${BP.tablet}) {
    font-size: ${FS.md};
  }
`;

const ModalSubtitle = styled.p`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.sm};
  color: ${T.mute};
  margin: 0;
`;

const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1.5px solid ${T.line};
  background: ${T.white};
  display: grid;
  place-items: center;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 150ms,
    border-color 150ms;

  &:hover {
    background: ${T.soft};
    border-color: ${T.subtle};
  }
`;

const StepStrip = styled.div`
  display: flex;
  align-items: stretch;
  padding: 0;
  background: ${T.white};
  border-bottom: 1px solid ${T.line};
  flex-shrink: 0;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StepConnector = styled.div`
  flex: 1;
  height: 2px;
  background: ${(p) => (p.$done ? T.teal : T.line)};
  align-self: center;
  min-width: 12px;
  transition: background 300ms ease;
`;

const StepTabWrap = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 18px 12px;
  font-family: ${(p) =>
    p.$active ? FONTS.primaryFontBold : FONTS.primaryFontRegular};
  font-size: ${FS.xs};
  font-weight: ${(p) => (p.$active ? 700 : 400)};
  color: ${(p) => (p.$active ? T.teal : p.$done ? T.navy : T.mute)};
  background: none;
  border: none;
  border-bottom: 2.5px solid ${(p) => (p.$active ? T.teal : "transparent")};
  cursor: ${(p) => (p.$done ? "pointer" : "default")};
  white-space: nowrap;
  transition:
    color 150ms,
    border-color 150ms;
  flex-shrink: 0;

  &:hover {
    color: ${(p) => (p.$done && !p.$active ? T.teal : undefined)};
  }

  @media (max-width: ${BP.mobile}) {
    padding: 10px 10px 8px;
    font-size: 10px;
    gap: 4px;
  }
`;

const StepNum = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: 700;
  display: grid;
  place-items: center;
  background: ${(p) => (p.$active ? T.teal : p.$done ? T.tealDark : T.line)};
  color: ${(p) => (p.$active || p.$done ? "#fff" : T.mute)};
  transition: background 200ms;
  flex-shrink: 0;
  box-shadow: ${(p) => (p.$active ? `0 0 0 4px ${T.tealTint}` : "none")};
  transition:
    box-shadow 200ms,
    background 200ms;
`;

export const StepProgressStrip = ({ step, stepLabels, onJump }) => (
  <StepStrip role="tablist" aria-label="Wizard steps">
    {stepLabels.map((label, idx) => {
      const s = idx + 1;
      const isLast = idx === stepLabels.length - 1;
      return (
        <React.Fragment key={s}>
          <StepTabWrap
            $active={s === step}
            $done={s < step}
            onClick={() => s < step && onJump(s)}
            role="tab"
            aria-selected={s === step}
          >
            <StepNum $active={s === step} $done={s < step}>
              {s < step ? <Icons.Check size={10} color="#fff" /> : s}
            </StepNum>
            {label}
          </StepTabWrap>
          {!isLast && <StepConnector $done={s < step} />}
        </React.Fragment>
      );
    })}
  </StepStrip>
);

export const ModalBody = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 28px 28px 20px;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${T.line};
    border-radius: 3px;
  }

  @media (max-width: ${BP.tablet}) {
    padding: 18px 16px;
  }
`;

const FooterWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid ${T.line};
  background: ${T.soft};
  flex-shrink: 0;

  @media (max-width: ${BP.tablet}) {
    padding: 12px 16px;
  }
`;

const StepDotRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StepDot = styled.div`
  height: 5px;
  border-radius: 3px;
  background: ${(p) => (p.$on ? T.teal : T.line)};
  width: ${(p) => (p.$on ? "20px" : "8px")};
  transition: all 250ms cubic-bezier(0.34, 1.56, 0.64, 1);
`;

export const FooterRight = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FooterBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: ${(p) => (p.$primary ? "10px 20px" : "9px 16px")};
  border-radius: 10px;
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.sm};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1.5px solid transparent;

  ${(p) =>
    p.$primary
      ? css`
          background: linear-gradient(135deg, ${T.teal} 0%, ${T.tealDark} 100%);
          color: #fff;
          border-color: ${T.tealDark};
          box-shadow: 0 4px 12px rgba(0, 131, 140, 0.3);
          &:hover:not(:disabled) {
            box-shadow: 0 6px 18px rgba(0, 131, 140, 0.42);
            transform: translateY(-1px);
          }
          &:disabled {
            opacity: 0.45;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
          }
        `
      : css`
          background: ${T.white};
          color: ${T.ink};
          border-color: ${T.line};
          &:hover {
            background: ${T.soft};
            border-color: ${T.subtle};
          }
        `}
`;

export const ModalFooter = ({
  step,
  totalSteps,
  canNext,
  onBack,
  onNext,
  onClose,
  onCreate,
}) => (
  <FooterWrap>
    <StepDotRow aria-hidden="true">
      {Array.from({ length: totalSteps }, (_, i) => (
        <StepDot key={i} $on={i < step} />
      ))}
    </StepDotRow>

    <FooterBtn onClick={onClose}>Cancel</FooterBtn>

    <FooterRight>
      {step > 1 && (
        <FooterBtn onClick={onBack}>
          <Icons.ChevronLeft size={13} color={T.ink} />
          Back
        </FooterBtn>
      )}
      {step < totalSteps && (
        <FooterBtn $primary onClick={onNext} disabled={!canNext}>
          Next
          <Icons.ChevronRight size={13} color="#fff" />
        </FooterBtn>
      )}
      {step === totalSteps && (
        <FooterBtn $primary onClick={onCreate} disabled={!canNext}>
          Create Bundle
        </FooterBtn>
      )}
    </FooterRight>
  </FooterWrap>
);
